import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import { SUPPORT } from '../lib/support';
import Icon from './Icon';

// ============================================================
// AI ASISTENT — plutajuće dugme dole desno i prozor za razgovor.
// Poruke idu Supabase funkciji "assistant" (Claude), ključ ostaje na serveru.
// ============================================================

const MAX_HISTORY = 12; // koliko poslednjih poruka se šalje (manje = jeftinije)

export default function AssistantWidget() {
  const { lang, tr } = useI18n();
  const { activeAgencyId } = useAgency();
  const navigate = useNavigate();
  // ---------- glas (diktiranje) — prepoznavanje govora u pregledaču ----------
  const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const sendRef = useRef(null);
  const [micHint, setMicHint] = useState('');
  const IOS = typeof navigator !== 'undefined' && (/iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user' | 'assistant', content }
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Otvaranje iz drugih delova aplikacije (npr. "Chat podrška")
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('finart:open-assistant', onOpen);
    return () => window.removeEventListener('finart:open-assistant', onOpen);
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) setTimeout(() => inputRef.current?.focus(), 50);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setError('');
    const next = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setInput('');
    setBusy(true);
    const { data, error: fnErr } = await supabase.functions.invoke('assistant', {
      body: { messages: next.slice(-MAX_HISTORY).map(({ role, content }) => ({ role, content })), lang, agency_id: activeAgencyId },
    });
    setBusy(false);
    if (fnErr || !data?.reply) {
      let msg = data?.error || fnErr?.message || '';
      try { const b = await fnErr?.context?.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
      setError(`${tr('Asistent trenutno ne može da odgovori.', 'The assistant cannot reply right now.', 'Ассистент сейчас не может ответить.')} ${msg}`);
      return;
    }
    setMessages((m) => [...m, { role: 'assistant', content: data.reply, cards: data.cards || [] }]);
    // Asistent je možda napravio fakturu ili upisao pazar — osveži brojače u aplikaciji
    if ((data.cards || []).some((c) => c.type === 'invoice' || c.to === '/pazar')) window.dispatchEvent(new Event('finart:data-changed'));
  };
  sendRef.current = send;

  const toggleMic = () => {
    if (!SR) return;
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SR();
    rec.lang = { sr: 'sr-RS', en: 'en-US', ru: 'ru-RU' }[lang] || 'sr-RS';
    rec.interimResults = true;
    rec.continuous = false;
    let finalText = '';
    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      setInput((finalText + interim).trim());
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      if (IOS) {
        setMicHint(tr(
          'Na iPhone-u: uključite Podešavanja → Opšte → Tastatura → Diktiranje, i dozvolite mikrofon za ovaj sajt (u Safariju „aA“ → Podešavanja veb-sajta → Mikrofon). Najlakše: dodirnite polje za poruku i koristite 🎤 na samoj tastaturi.',
          'On iPhone: turn on Settings → General → Keyboard → Dictation and allow the microphone for this site (Safari “aA” → Website Settings → Microphone). Easiest: tap the message field and use 🎤 on the keyboard.',
          'На iPhone: включите Настройки → Основные → Клавиатура → Диктовка и разрешите микрофон для сайта. Проще всего — 🎤 на клавиатуре.'));
      } else if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setMicHint(tr('Dozvolite pristup mikrofonu: kliknite na katanac pored adrese sajta → Mikrofon → Dozvoli, pa probajte ponovo.', 'Allow the microphone: click the lock next to the address → Microphone → Allow, then try again.', 'Разрешите микрофон: замок рядом с адресом → Микрофон → Разрешить.'));
      } else {
        setMicHint(tr('Glasovni unos trenutno ne radi u ovom pregledaču. Pokušajte u Chrome-u ili Safariju, ili napišite poruku.', 'Voice input does not work in this browser right now. Try Chrome or Safari, or type.', 'Голосовой ввод не работает в этом браузере.'));
      }
    };
    rec.onend = () => {
      setListening(false);
      const t = finalText.trim();
      if (t) sendRef.current?.(t); // izgovoreno se odmah šalje
    };
    recRef.current = rec;
    setError(''); setMicHint('');
    setListening(true);
    try { rec.start(); } catch { setListening(false); }
  };
  useEffect(() => () => recRef.current?.abort?.(), []);

  const go = (to) => { navigate(to); if (window.matchMedia('(max-width: 600px)').matches) setOpen(false); };
  const money = (n, cur) => `${new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)} ${cur || 'RSD'}`;
  const Card = ({ c }) => {
    if (c.type === 'invoice') {
      return (
        <div style={styles.card}>
          <div style={styles.cardTop}>
            <span style={styles.cardNo}><Icon name="fileText" size={14} />{c.number}</span>
            <span style={styles.draft}>{tr('Nacrt', 'Draft', 'Черновик')}</span>
          </div>
          <div style={styles.cardClient}>{c.client}</div>
          <div style={styles.cardAmount}>{money(c.amount, c.currency)}</div>
          <div style={styles.cardBtns}>
            <button onClick={() => go(`/fakturisanje/${c.id}/izmeni`)} style={styles.cardBtn}><Icon name="pencil" size={13} />{tr('Izmeni', 'Edit', 'Изменить')}</button>
            <button onClick={() => go(`/fakturisanje/${c.id}/pregled`)} style={{ ...styles.cardBtn, color: 'var(--lavender-700)' }}>{tr('Pregled', 'View', 'Просмотр')} ›</button>
          </div>
        </div>
      );
    }
    if (c.type === 'limit') {
      const pct = Math.min(c.pct, 100);
      return (
        <div style={styles.card}>
          <div style={styles.cardTop}><span className="mono-label">{tr('Paušalni limit', 'Flat-rate limit', 'Лимит')}</span><strong style={{ color: 'var(--lavender-700)' }}>{String(c.pct).replace('.', ',')}%</strong></div>
          <div style={styles.bar}><span style={{ ...styles.barFill, width: `${pct}%` }} /></div>
          <div style={styles.cardRow}><span>{money(c.invoiced)}</span><span>{tr('Ostalo', 'Left', 'Осталось')}: {money(c.remaining)}</span></div>
          <div style={styles.cardSub}>{c.projection}</div>
        </div>
      );
    }
    if (c.type === 'link') {
      return (
        <button onClick={() => go(c.to)} style={styles.linkCard}>
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <strong style={{ fontSize: 13.5 }}>{c.title}</strong>
            {c.sub && <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>{c.sub}</span>}
          </span>
          <span style={{ color: 'var(--lavender-700)', fontWeight: 700, fontSize: 13 }}>{tr('Otvori', 'Open', 'Открыть')} ›</span>
        </button>
      );
    }
    return null;
  };

  const suggestions = [
    tr('Koliko mi je ostalo do limita?', 'How much is left to my limit?', 'Сколько осталось до лимита?'),
    tr('Ponovi poslednju fakturu za…', 'Repeat the last invoice for…', 'Повтори последний счёт для…'),
    tr('Kad mi ide porez?', 'When is my tax due?', 'Когда платить налог?'),
  ];

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} style={styles.fab} className="no-print assistant-fab"
          aria-label={tr('Otvori AI asistenta', 'Open AI assistant', 'Открыть AI-ассистента')}
          title={tr('FinArt asistent', 'FinArt assistant', 'FinArt-ассистент')}>
          <Icon name="message" size={24} color="#fff" />
        </button>
      )}

      {open && (
        <div style={styles.panel} className="no-print assistant-panel" role="dialog" aria-label={tr('FinArt asistent', 'FinArt assistant', 'FinArt-ассистент')}>
          <div style={styles.head}>
            <span style={styles.headIcon}><Icon name="message" size={18} color="#fff" /></span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={styles.headTitle}>{tr('FinArt asistent', 'FinArt assistant', 'FinArt-ассистент')}</span>
              <span style={styles.headSub}>AI · {tr('odgovara odmah', 'instant answers', 'отвечает сразу')}</span>
            </span>
            {messages.length > 0 && (
              <button onClick={() => { setMessages([]); setError(''); }} style={styles.headBtn}
                title={tr('Nov razgovor', 'New chat', 'Новый разговор')} aria-label={tr('Nov razgovor', 'New chat', 'Новый разговор')}>
                <Icon name="plus" size={17} color="#fff" />
              </button>
            )}
            <button onClick={() => setOpen(false)} style={styles.headBtn} aria-label={tr('Zatvori', 'Close', 'Закрыть')}>
              <Icon name="x" size={17} color="#fff" />
            </button>
          </div>

          <div ref={listRef} style={styles.list} aria-live="polite">
            <div style={{ ...styles.bubble, ...styles.bot }}>
              {tr(
                'Zdravo! 👋 Ja sam FinArt asistent. Mogu i da radim umesto tebe: „Napravi fakturu za ABC doo, 56.000 za mart“, „Ponovi fakturu za …“, „Pazar mi je danas 21.000“, „Koliko mi je ostalo do limita?“. Možeš i da mi kažeš glasom 🎤.',
                "Hi! 👋 I'm the FinArt assistant. I can also do things for you: “Create an invoice for ABC doo, 56,000 for March”, “Repeat the invoice for …”, “Today's takings are 21,000”, “How much is left to my limit?”. You can also speak 🎤.",
                'Здравствуйте! 👋 Я FinArt-ассистент. Могу и действовать: «Создай счёт для ABC doo, 56 000 за март», «Повтори счёт для …», «Выручка сегодня 21 000», «Сколько до лимита?». Можно голосом 🎤.',
              )}
            </div>
            {messages.length === 0 && (
              <div style={styles.chips}>
                {suggestions.map((s) => <button key={s} onClick={() => send(s)} style={styles.chip}>{s}</button>)}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{ ...styles.bubble, ...(m.role === 'user' ? styles.me : styles.bot) }}>{m.content}</div>
                {(m.cards || []).map((c, j) => <Card key={j} c={c} />)}
              </div>
            ))}
            {micHint && <div style={styles.hint}>{micHint}</div>}
            {busy && <div style={{ ...styles.bubble, ...styles.bot, color: 'var(--ink-500)' }}>{tr('Pišem odgovor...', 'Typing...', 'Печатаю ответ...')}</div>}
            {error && (
              <div style={styles.error}>
                {error} {tr('Možeš nas i pozvati:', 'You can also call us:', 'Можно позвонить нам:')} <a href={`tel:${SUPPORT.phone}`}>{SUPPORT.phoneDisplay}</a>
              </div>
            )}
          </div>

          <div style={styles.inputRow}>
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={listening ? tr('Slušam…', 'Listening…', 'Слушаю…') : tr('Napiši ili izgovori…', 'Type or speak…', 'Напишите или скажите…')}
              aria-label={tr('Pitanje za asistenta', 'Question for the assistant', 'Вопрос ассистенту')}
              style={styles.input}
              maxLength={1500}
            />
            {SR && (
              <button onClick={toggleMic} disabled={busy} style={{ ...styles.micBtn, ...(listening ? styles.micOn : {}) }}
                aria-label={listening ? tr('Zaustavi', 'Stop', 'Стоп') : tr('Govori', 'Speak', 'Говорить')} title={listening ? tr('Zaustavi', 'Stop', 'Стоп') : tr('Govori', 'Speak', 'Говорить')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /></svg>
              </button>
            )}
            <button onClick={() => send()} disabled={!input.trim() || busy} style={{ ...styles.sendBtn, opacity: input.trim() && !busy ? 1 : 0.5 }}
              aria-label={tr('Pošalji', 'Send', 'Отправить')}>
              <Icon name="arrowLeft" size={18} color="#fff" style={{ transform: 'rotate(180deg)' }} />
            </button>
          </div>
          <div style={styles.disclaimer}>
            {tr(
              'AI može da pogreši. Za poreske odluke proveri sa knjigovođom ili Poreskom upravom.',
              'AI can make mistakes. Check tax decisions with an accountant or the Tax Administration.',
              'AI может ошибаться. Налоговые решения проверяйте у бухгалтера или в налоговой.',
            )}
          </div>
        </div>
      )}
      <style>{`
        /* Telefon i tablet: dugme i prozor iznad donje trake sa karticama */
        @media (max-width: 1023px) {
          .assistant-fab { bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; right: 16px !important; width: 52px !important; height: 52px !important; }
          .assistant-panel { bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; max-height: calc(100vh - 120px) !important; }
        }
        @media (max-width: 600px) {
          .assistant-panel { right: 0 !important; left: 0 !important; bottom: 0 !important; width: 100% !important; height: 88vh !important; border-radius: 18px 18px 0 0 !important; }
        }
      `}</style>
    </>
  );
}

const styles = {
  fab: { position: 'fixed', right: 22, bottom: 'calc(22px + env(safe-area-inset-bottom, 0px))', width: 58, height: 58, borderRadius: '50%', border: 'none', background: 'var(--lavender-700)', boxShadow: '0 10px 26px rgba(109,91,208,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 45, cursor: 'pointer' },
  panel: { position: 'fixed', right: 22, bottom: 'calc(22px + env(safe-area-inset-bottom, 0px))', width: 380, height: 560, maxHeight: 'calc(100vh - 44px)', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', zIndex: 45, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 12px 12px 14px', background: 'var(--teal-800)', color: '#fff' },
  headIcon: { width: 34, height: 34, borderRadius: '50%', background: 'var(--lavender-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  headTitle: { display: 'block', fontWeight: 800, fontSize: 15 },
  headSub: { display: 'block', fontSize: 11.5, opacity: 0.8 },
  headBtn: { width: 34, height: 34, borderRadius: 10, border: 'none', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  list: { flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--paper)' },
  bubble: { maxWidth: '86%', padding: '10px 13px', borderRadius: 10, fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  bot: { alignSelf: 'flex-start', background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink-900)', borderBottomLeftRadius: 4 },
  me: { alignSelf: 'flex-end', background: 'var(--teal-800)', color: '#fff', borderBottomRightRadius: 4 },
  chips: { display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' },
  chip: { border: '1px solid var(--lavender-500)', background: 'var(--lavender-100)', color: 'var(--lavender-700)', borderRadius: 999, padding: '7px 12px', fontSize: 13, fontWeight: 600, textAlign: 'left' },
  error: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '8px 10px', fontSize: 12.5 },
  inputRow: { display: 'flex', gap: 8, padding: '10px 12px', borderTop: '1px solid var(--line)', background: 'var(--surface)' },
  input: { flex: 1, resize: 'none', border: '1.5px solid var(--line)', borderRadius: 8, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', maxHeight: 110, minHeight: 42 },
  hint: { background: 'var(--lavender-100)', color: 'var(--ink-900)', borderRadius: 10, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5 },
  micBtn: { width: 42, height: 42, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-end', cursor: 'pointer' },
  micOn: { background: 'var(--lavender-700)', borderColor: 'var(--lavender-700)', color: '#fff', boxShadow: '0 0 0 4px var(--lavender-100)' },
  card: { width: '86%', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, alignSelf: 'flex-start' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  cardNo: { display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 14 },
  draft: { fontSize: 11, fontWeight: 600, color: 'var(--ink-500)', background: 'var(--paper)', borderRadius: 999, padding: '2px 8px' },
  cardClient: { fontSize: 12.5, color: 'var(--ink-500)' },
  cardAmount: { fontSize: 19, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  cardBtns: { display: 'flex', borderTop: '1px solid var(--line)', marginTop: 2 },
  cardBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 34, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 600, color: 'var(--ink-700)', cursor: 'pointer' },
  bar: { height: 6, borderRadius: 99, background: 'var(--line)', overflow: 'hidden' },
  barFill: { display: 'block', height: '100%', background: 'var(--lavender-700)' },
  cardRow: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ink-700)', fontVariantNumeric: 'tabular-nums' },
  cardSub: { fontSize: 12, color: 'var(--ink-500)', borderTop: '1px solid var(--line)', paddingTop: 6 },
  linkCard: { width: '86%', alignSelf: 'flex-start', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px', textAlign: 'left', cursor: 'pointer' },
  sendBtn: { width: 42, height: 42, borderRadius: 8, border: 'none', background: 'var(--lavender-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-end' },
  disclaimer: { fontSize: 10.5, color: 'var(--ink-500)', textAlign: 'center', padding: '0 12px 10px', background: 'var(--surface)' },
};
